import * as React from 'react';
import { Icon } from '../../atomics/Icon/Icon';
import type { LucideIconName } from '../../atomics/Icon/Icon';
import { Tabs as TabsRoot, TabsList, TabsTrigger } from '../../shadcn/tabs';

export interface NavTabItem {
  value: string;
  label: React.ReactNode;
  href: string;
  /** Lucide icon name (e.g. "calculator", "map"). */
  icon?: LucideIconName;
  disabled?: boolean;
}

export interface NavTabsProps {
  tabs: NavTabItem[];
  activeValue: string;
  /**
   * Link component used for navigation. Pass Next.js `Link` in Next.js apps.
   * Defaults to a standard `<a>` tag.
   */
  linkComponent?: React.ElementType;
  className?: string;
}

/**
 * Card-style navigation tabs backed by Radix UI Tabs primitives.
 * Each tab is a navigable link — pass `linkComponent={Link}` for client-side routing.
 */
export function NavTabs({
  tabs,
  activeValue,
  linkComponent: LinkComponent = 'a',
  className,
}: NavTabsProps) {
  return (
    <TabsRoot value={activeValue} className={className}>
      <TabsList className='h-auto flex-wrap justify-start gap-2'>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            asChild
            className='inline-flex min-h-[56px] min-w-[120px] max-w-[220px] items-center gap-2 rounded-[8px] border border-neutral-200 p-3 font-body text-b5 font-normal text-black no-underline data-[state=active]:bg-neutral-50 data-[state=inactive]:bg-white data-[state=inactive]:hover:bg-neutral-50/50'
          >
            <LinkComponent href={tab.href}>
              {tab.icon && (
                <span className='flex h-8 w-8 shrink-0 items-center justify-center'>
                  <Icon
                    icon={tab.icon}
                    size='lg'
                    color={activeValue === tab.value ? 'primary' : 'default'}
                    aria-hidden
                  />
                </span>
              )}
              <span className='min-w-0 break-words text-left text-wrap leading-4 [margin-block-end:var(--paragraph-spacing-b5,0)]'>
                {tab.label}
              </span>
            </LinkComponent>
          </TabsTrigger>
        ))}
      </TabsList>
    </TabsRoot>
  );
}
